// Cookie Settings utility for managing Transcend cookie preferences
// Converted from jQuery to vanilla JavaScript for React compatibility

export const initializeCookieSettings = () => {
  // DNSS/CPRA localized messages mapping
  const getDnssLocalizedText = (browserLang) => {
    const dnssMessages = [
      { codes: ['en-US'], message: 'Do not sell or share my information'},
      { codes: ['fr', 'fr-FR'], message: 'Refuser la vente et le partage de vos informations'},
      { codes: ['ja', 'jp', 'ja-JP'], message: '私の情報を販売または共有しないでください'},
      { codes: ['es', 'es-ES'], message: 'No vender ni compartir mi información'},
      { codes: ['de', 'de-DE'], message: 'Meine Informationen nicht verkaufen oder teilen'},
      { codes: ['it', 'it-IT'], message: 'Non vendere o condividere i miei dati'},
      { codes: ['pt', 'pt-BR'], message: 'Não vender ou compartilhar minhas informações'},
      { codes: ['ko', 'ko-KR'], message: '내 정보 판매 또는 공유 금지'},
      { codes: ['zh', 'zh-CN'], message: '不得出售或共享我的信息'},
      { codes: ['zh-TW', 'zh-HK'], message: '不得出售或分享我的資訊'},
    ];
    const messageObj = dnssMessages.find(msg => msg.codes.includes(browserLang));
    return messageObj ? messageObj.message : 'Do not sell or share my information';
  };

  // GDPR localized messages mapping
  const getGdprLocalizedText = (browserLang) => {
    const gdprMessages = [
      { codes: ['en-US'], message: 'Necessary cookies only'},
      { codes: ['fr', 'fr-FR'], message: 'Seulement les cookies nécessaires'},
      { codes: ['ja', 'jp', 'ja-JP'], message: '必須のクッキーのみを許可'},
      { codes: ['es', 'es-ES'], message: 'Solo las cookies necesarias'},
      { codes: ['de', 'de-DE'], message: 'Nur notwendige Cookies'},
      { codes: ['it', 'it-IT'], message: 'Solo i cookie necessari'},
      { codes: ['pt', 'pt-BR'], message: 'Somente os cookies necessários'},
      { codes: ['ko', 'ko-KR'], message: '필수 쿠키만 허용'},
      { codes: ['zh', 'zh-CN'], message: '仅必要 Cookie'},
      { codes: ['zh-TW', 'zh-HK'], message: '僅限必要的 Cookie'},
    ];
    const cookieMessageObj = gdprMessages.find(msg => msg.codes.includes(browserLang));
    return cookieMessageObj ? cookieMessageObj.message : 'Necessary cookies only';
  };

  // Get user's browser language
  const getUserLanguage = () => {
    return navigator.language || navigator.userLanguage || 'en-US';
  };

  // Setup Transcend UI variables based on region
  const setupTranscendUI = (userBrowserLanguage) => {
    // Check if user is in DNSS/CPRA region
    const isDnssRegion = window.airgap && window.airgap.getRegimes && window.airgap.getRegimes().has('US_DNSS');
    
    if (isDnssRegion) {
      // DNSS/CPRA region - set button texts accordingly
      if (typeof window.transcend !== 'undefined') {
        window.transcend.setUiVariables({
          rejectAllButtonText: 'Do not sell or share my information',
          rejectAllFrButtonText: 'Refuser la vente et le partage de vos informations',
          rejectAllJpButtonText: '私の情報を販売または共有しないでください',
          rejectAllEsButtonText: 'No vender ni compartir mi información',
          rejectAllDeButtonText: 'Meine Informationen nicht verkaufen oder teilen',
          rejectAllItButtonText: 'Non vendere o condividere i miei dati',
          rejectAllPtButtonText: 'Não vender ou compartilhar minhas informações',
          rejectAllKoButtonText: '내 정보 판매 또는 공유 금지',
          rejectAllZhCnButtonText: '不得出售或共享我的信息',
          rejectAllZhTwButtonText: '不得出售或分享我的資訊',
        });
      }
      
      // Update footer link text for DNSS region
      const cookieSettingsLink = document.getElementById('tcm-sdk-show-settings');
      if (cookieSettingsLink) {
        const dnssLocalizedText = getDnssLocalizedText(userBrowserLanguage);
        cookieSettingsLink.textContent = dnssLocalizedText;
      }
    } else {
      // GDPR region - set button texts accordingly
      if (typeof window.transcend !== 'undefined') {
        window.transcend.setUiVariables({
          rejectAllButtonText: 'Necessary cookies only',
          rejectAllFrButtonText: 'Seulement les cookies nécessaires',
          rejectAllJpButtonText: '必須のクッキーのみを許可',
          rejectAllEsButtonText: 'Solo las cookies necesarias',
          rejectAllDeButtonText: 'Nur notwendige Cookies',
          rejectAllItButtonText: 'Solo i cookie necessari',
          rejectAllPtButtonText: 'Somente os cookies necessários',
          rejectAllKoButtonText: '필수 쿠키만 허용',
          rejectAllZhCnButtonText: '仅必要 Cookie',
          rejectAllZhTwButtonText: '僅限必要的 Cookie'
        });
      }
    }
  };

  // Setup click handler for cookie settings link
  const setupClickHandler = () => {
    const cookieSettingsLink = document.getElementById('tcm-sdk-show-settings');
    if (cookieSettingsLink) {
      cookieSettingsLink.addEventListener('click', function(e) {
        e.preventDefault();
        if (typeof window.transcend !== 'undefined') {
          window.transcend.showConsentManager({ viewState: 'CompleteOptions' });
        }
      });
    }
  };

  // Initialize when DOM is ready
  const initialize = () => {
    const initializeAfterDOMAndFooter = () => {
      const userBrowserLanguage = getUserLanguage();
      
      // Wait for Transcend to be available
      const waitForTranscend = (attempts = 0) => {
        if (attempts > 50) { // Max 5 seconds wait
          console.warn('Transcend library not available after 5 seconds');
          return;
        }
        
        if (typeof window.transcend !== 'undefined' || (window.airgap && window.airgap.getRegimes)) {
          setupTranscendUI(userBrowserLanguage);
          setupClickHandler();
        } else {
          setTimeout(() => waitForTranscend(attempts + 1), 100);
        }
      };
      
      waitForTranscend();
    };

    // Ensure DOM is ready and footer is rendered
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        // Additional delay to ensure footer is rendered
        setTimeout(initializeAfterDOMAndFooter, 100);
      });
    } else {
      // DOM is already ready, but still wait a bit for footer rendering
      setTimeout(initializeAfterDOMAndFooter, 100);
    }

    // Also initialize when page is fully loaded
    window.addEventListener('load', () => {
      setTimeout(initializeAfterDOMAndFooter, 500);
    });
  };

  return {
    initialize,
    setupTranscendUI,
    setupClickHandler,
    getUserLanguage,
    getDnssLocalizedText,
    getGdprLocalizedText
  };
};

// Auto-initialize when module is imported
export const cookieSettings = initializeCookieSettings();
